/**
 * Admin section shell.
 *
 * Mounted at /admin (gated by RequireAuth + RequireRole('admin') in
 * App.tsx). Owns the page chrome (Premium header + sticky tabs) and
 * delegates each tab's body to a nested route via <Outlet/>.
 *
 * Premium pass:
 *  - Gold-accented tab indicator (was indigo) to match the brand.
 *  - Single "Setup Pipeline" panel — hero total + segmented bar +
 *    clickable legend chips that filter the users table.  Replaces
 *    the older 4-equal-card KPI strip; the bar visualizes the
 *    onboarded / pending / expired distribution at a glance.
 *  - Section header treatment matches the Profile / auth pages
 *    (Inter Semibold, -0.015em tracking, gold-underlined overline).
 */

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box, Container, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { fetchUsers } from '../../api/admin';
import { SiteLayout } from '../../components/SiteLayout';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const TABS = [
  { value: 'users', label: 'Users', icon: GroupIcon, path: '/admin/users' },
  { value: 'roles', label: 'Roles', icon: VerifiedUserIcon, path: '/admin/roles' },
  {
    value: 'announcements',
    label: 'Announcements',
    icon: CampaignOutlinedIcon,
    path: '/admin/announcements',
  },
  {
    value: 'data',
    label: 'Data Uploads',
    icon: CloudUploadOutlinedIcon,
    path: '/admin/data-uploads',
  },
  {
    value: 'ask-abhi',
    label: 'Ask ABHI',
    icon: AutoAwesomeOutlinedIcon,
    path: '/admin/ask-abhi',
  },
  { value: 'audit', label: 'Audit Log', icon: HistoryIcon, path: '/admin/audit-log' },
] as const;

type TabValue = (typeof TABS)[number]['value'];

function tabValueFromPath(pathname: string): TabValue {
  if (pathname.startsWith('/admin/roles')) return 'roles';
  if (pathname.startsWith('/admin/announcements')) return 'announcements';
  if (pathname.startsWith('/admin/audit-log')) return 'audit';
  if (pathname.startsWith('/admin/data-uploads')) return 'data';
  if (pathname.startsWith('/admin/ask-abhi')) return 'ask-abhi';
  // /admin/pending used to be its own tab — it now folds into the
  // users view filtered to status=pending.  Treat it as Users for
  // the active-tab indicator.
  return 'users';
}

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = tabValueFromPath(location.pathname);

  // Which KPI card matches the current view? Drives the visual
  // "selected" affordance so admins always see at a glance which
  // slice the table below is showing.  The setup-pipeline cards
  // (Onboarded / Pending Setup / Link Expired) all live on the
  // Users tab and select via the ``setup`` URL param.
  const onUsersTab = location.pathname.startsWith('/admin/users');
  const usersStatusParam = searchParams.get('status');
  const usersSetupParam = searchParams.get('setup');
  type ActiveKpi = 'total' | 'onboarded' | 'pending_setup' | 'link_expired' | 'awaiting_approval';
  const activeKpi: ActiveKpi | null = (() => {
    if (!onUsersTab) return null;
    if (usersStatusParam === 'pending') return 'awaiting_approval';
    if (usersSetupParam === 'completed') return 'onboarded';
    if (usersSetupParam === 'pending_setup') return 'pending_setup';
    if (usersSetupParam === 'link_expired') return 'link_expired';
    if (!usersStatusParam || usersStatusParam === 'all') return 'total';
    return null;
  })();

  // Lightweight queries for the setup-pipeline KPI strip.  Counts only;
  // each tab still owns its own paginated data fetching.  All cards
  // exclude soft-deleted users (backend filters them out by default
  // when no status is passed).
  const totals = useQuery({
    // Nested under ['admin', 'users'] so the existing user-mutation
    // invalidations (create / delete / role change / etc.) re-fetch
    // the KPI strip without each mutation having to opt in.
    queryKey: ['admin', 'users', 'metrics', 'setup-pipeline'],
    queryFn: async () => {
      const [allRes, completedRes, pendingSetupRes, linkExpiredRes, awaitingRes] =
        await Promise.all([
          fetchUsers({ limit: 1, status: undefined }),
          fetchUsers({ limit: 1, setup_status: 'completed' }),
          fetchUsers({ limit: 1, setup_status: 'pending_setup' }),
          fetchUsers({ limit: 1, setup_status: 'link_expired' }),
          fetchUsers({ limit: 1, status: 'pending' }),
        ]);
      return {
        total: allRes.total,
        onboarded: completedRes.total,
        pending_setup: pendingSetupRes.total,
        link_expired: linkExpiredRes.total,
        awaiting_approval: awaitingRes.total,
      };
    },
    staleTime: 30_000,
  });

  return (
    <SiteLayout subtitle="Admin" showDesktopHint>
      <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 5 } }}>
        {/* ── Page heading ───────────────────────────────────── */}
        {/*  Section context ("Admin") lives in the global
            Header next to the logo, so the body heading sticks to the
            page-specific title + subtitle. Avoids the duplicate
            eyebrow problem and matches the pattern on the Sales /
            Marketing / Profile pages. */}
        <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: { xs: 24, md: 30 },
              letterSpacing: '-0.015em',
              lineHeight: 1.15,
              color: INK,
            }}
          >
            Manage Users, Roles &amp; Audit Trail
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: FONT_INTER,
              fontSize: 14,
              lineHeight: 1.55,
              color: 'rgba(15, 10, 7, 0.62)',
              maxWidth: 720,
            }}
          >
            Approve sign-ups, assign roles, manage the role catalog, and review the complete
            activity history.
          </Typography>

          <SetupPipelinePanel
            total={totals.data?.total}
            onboarded={totals.data?.onboarded}
            pendingSetup={totals.data?.pending_setup}
            linkExpired={totals.data?.link_expired}
            awaitingApproval={totals.data?.awaiting_approval}
            loading={totals.isLoading}
            activeFilter={
              activeKpi === 'onboarded'
                ? 'completed'
                : activeKpi === 'pending_setup'
                  ? 'pending_setup'
                  : activeKpi === 'link_expired'
                    ? 'link_expired'
                    : activeKpi === 'awaiting_approval'
                      ? 'awaiting_approval'
                      : activeKpi === 'total'
                        ? 'all'
                        : null
            }
            onSelect={(key) => {
              // Preserve any existing role / search params already on
              // the URL — but `setup` and `status` are owned by the
              // chips, so we drop them before re-applying the
              // selection.  Awaiting-approval is a `status=pending`
              // filter; the three setup-state chips drive `setup=`.
              const next = new URLSearchParams(onUsersTab ? searchParams : undefined);
              next.delete('setup');
              next.delete('status');
              if (key === 'awaiting_approval') next.set('status', 'pending');
              else if (key !== 'all') next.set('setup', key);
              const qs = next.toString();
              navigate(`/admin/users${qs ? `?${qs}` : ''}`);
            }}
          />
        </Box>

        {/* ── Tabs ───────────────────────────────────────────── */}
        <Box
          sx={{
            borderBottom: '1px solid rgba(15, 10, 7, 0.08)',
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            // Five tabs don't fit a phone: scroll them rather than clip the last ones.
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={(_, value: TabValue) => {
              const target = TABS.find((t) => t.value === value);
              if (target) navigate(target.path);
            }}
            sx={{
              minHeight: 44,
              '& .MuiTabs-indicator': {
                height: 2.5,
                borderRadius: '2px 2px 0 0',
                background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, ${GOLD} 100%)`,
              },
              '& .MuiTab-root': {
                minHeight: 44,
                fontFamily: FONT_INTER,
                fontWeight: 600,
                fontSize: { xs: 13, md: 14 },
                letterSpacing: '0.01em',
                textTransform: 'none',
                color: 'rgba(15, 10, 7, 0.5)',
                transition: 'color 180ms ease',
                '&:hover': { color: 'rgba(15, 10, 7, 0.78)' },
                '&.Mui-selected': { color: INK, fontWeight: 700 },
              },
            }}
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.value === activeTab;
              return (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon
                        sx={{
                          fontSize: 18,
                          color: isActive ? GOLD : 'rgba(15, 10, 7, 0.45)',
                          transition: 'color 180ms ease',
                        }}
                      />
                      {tab.label}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
      </Container>

      <Container maxWidth="xl" sx={{ pb: { xs: 4, md: 6 } }}>
        <Outlet />
      </Container>
    </SiteLayout>
  );
}

// ---------------------------------------------------------------------
// Setup-pipeline panel — at-a-glance roster health.
//
// Replaces the previous 4-equal-card KPI strip.  Renders the setup
// pipeline as a single editorial panel:
//   • Eyebrow + hero count of total non-deleted users.
//   • Segmented progress bar (onboarded / pending / expired) so the
//     distribution reads at a glance, not as four flat numbers.
//   • Clickable legend chips below the bar — each filters the table.
//   • When Link Expired > 0 the chip surfaces a red "Resend" hint;
//     when 0 it stays muted so the eye doesn't pick it up.
// ---------------------------------------------------------------------

type SetupFilterKey = 'all' | 'completed' | 'pending_setup' | 'link_expired' | 'awaiting_approval';

const ONBOARDED_GREEN = '#22865a';
const EXPIRED_RED = '#b22f2f';
const APPROVAL_INDIGO = '#5b6bc7';

interface SetupPipelinePanelProps {
  total: number | undefined;
  onboarded: number | undefined;
  pendingSetup: number | undefined;
  linkExpired: number | undefined;
  /** Count of users with DB ``status='pending'`` — sign-ups waiting
   *  for an admin to approve.  Replaces the old "Pending Approvals"
   *  tab; folds into the Users table when the chip is clicked. */
  awaitingApproval: number | undefined;
  loading: boolean;
  activeFilter: SetupFilterKey | null;
  onSelect: (key: SetupFilterKey) => void;
}

function SetupPipelinePanel({
  total,
  onboarded,
  pendingSetup,
  linkExpired,
  awaitingApproval,
  loading,
  activeFilter,
  onSelect,
}: SetupPipelinePanelProps) {
  const safeTotal = total ?? 0;
  const safeOnboarded = onboarded ?? 0;
  const safePending = pendingSetup ?? 0;
  const safeExpired = linkExpired ?? 0;
  const safeAwaiting = awaitingApproval ?? 0;
  const pct = (n: number) => (safeTotal === 0 ? 0 : Math.round((n / safeTotal) * 100));

  // Awaiting approval is special — it's only relevant when sign-ups
  // are queued for admin review.  Render the chip only when there's
  // actually something to act on (or while the query is still in
  // flight so we don't briefly hide it during a refetch).  When zero,
  // the panel reverts to the 3-chip setup-state layout.
  const showAwaiting = loading || safeAwaiting > 0;

  return (
    <Box
      sx={{
        mt: 3,
        position: 'relative',
        bgcolor: '#fff',
        border: '1px solid rgba(15, 10, 7, 0.08)',
        borderRadius: 2,
        px: { xs: 2, md: 2.75 },
        py: { xs: 2, md: 2.25 },
        overflow: 'hidden',
        // Top gold accent rule echoes the editorial cards elsewhere
        // (Profile, admin dialogs) so the panel reads as part of the
        // same brand language.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
        },
      }}
    >
      {/* Header: eyebrow + hero total.  No "View all" pill — when no
          filter is selected, the table below is already showing the
          full roster, so the button only added noise.  A quiet
          "Clear filter" affordance appears inline next to the total
          whenever a setup filter IS active. */}
      <Box sx={{ mb: 1.75 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          User Setup Pipeline
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            mt: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: { xs: 26, md: 30 },
              fontWeight: 700,
              color: INK,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              opacity: loading ? 0.35 : 1,
            }}
          >
            {total !== undefined ? total : '—'}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.5)',
            }}
          >
            Total Users
          </Typography>
          {activeFilter && activeFilter !== 'all' && (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => onSelect('all')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect('all');
                }
              }}
              sx={{
                ml: 1,
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 600,
                color: GOLD,
                cursor: 'pointer',
                userSelect: 'none',
                borderBottom: '1px dotted rgba(187, 140, 63, 0.5)',
                transition: 'color 160ms ease, border-color 160ms ease',
                '&:hover': {
                  color: '#8e6a2a',
                  borderBottomColor: GOLD,
                },
                '&:focus-visible': {
                  outline: `2px solid ${GOLD}`,
                  outlineOffset: 2,
                  borderRadius: 1,
                },
              }}
            >
              Clear filter
            </Box>
          )}
        </Box>
      </Box>

      {/* Segmented distribution bar.  Each segment animates its width
          on data changes so the visual updates feel deliberate. */}
      <Box
        sx={{
          position: 'relative',
          height: 10,
          borderRadius: 999,
          bgcolor: 'rgba(15, 10, 7, 0.06)',
          overflow: 'hidden',
          display: 'flex',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: `${pct(safeOnboarded)}%`,
            bgcolor: ONBOARDED_GREEN,
            transition: 'width 320ms ease',
          }}
        />
        <Box
          sx={{
            width: `${pct(safePending)}%`,
            bgcolor: GOLD,
            transition: 'width 320ms ease',
          }}
        />
        <Box
          sx={{
            width: `${pct(safeExpired)}%`,
            bgcolor: EXPIRED_RED,
            transition: 'width 320ms ease',
          }}
        />
      </Box>

      {/* Legend chips — clickable filters into the users table.
          Three setup states always; awaiting-approval renders only
          when count > 0 (or while loading) so the panel stays clean
          on days when nothing's queued.  Stacks to one column on
          phones, two on small tablets, three or four on desktop. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: showAwaiting ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
          },
          gap: 1.25,
        }}
      >
        <LegendChip
          color={ONBOARDED_GREEN}
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
          count={onboarded}
          pct={pct(safeOnboarded)}
          label="Onboarded"
          tooltip={
            activeFilter === 'completed'
              ? 'Click to clear the filter and show all users'
              : 'Show users who have completed setup and signed in'
          }
          active={activeFilter === 'completed'}
          loading={loading}
          onClick={() => onSelect(activeFilter === 'completed' ? 'all' : 'completed')}
        />
        <LegendChip
          color={GOLD}
          icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
          count={pendingSetup}
          pct={pct(safePending)}
          label="Pending setup"
          tooltip={
            activeFilter === 'pending_setup'
              ? 'Click to clear the filter and show all users'
              : 'Invitation link is still valid — waiting for the user to claim'
          }
          active={activeFilter === 'pending_setup'}
          loading={loading}
          onClick={() => onSelect(activeFilter === 'pending_setup' ? 'all' : 'pending_setup')}
        />
        <LegendChip
          color={EXPIRED_RED}
          icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
          count={linkExpired}
          pct={pct(safeExpired)}
          label="Link expired"
          tooltip={
            activeFilter === 'link_expired'
              ? 'Click to clear the filter and show all users'
              : 'Invitation link has expired — resend from the kebab menu'
          }
          active={activeFilter === 'link_expired'}
          loading={loading}
          attention={safeExpired > 0}
          attentionBadge="Resend"
          onClick={() => onSelect(activeFilter === 'link_expired' ? 'all' : 'link_expired')}
        />
        {showAwaiting && (
          <LegendChip
            color={APPROVAL_INDIGO}
            icon={<PendingActionsIcon sx={{ fontSize: 16 }} />}
            count={awaitingApproval}
            pct={pct(safeAwaiting)}
            label="Awaiting approval"
            tooltip={
              activeFilter === 'awaiting_approval'
                ? 'Click to clear the filter and show all users'
                : 'Sign-ups waiting for an admin to approve or reject'
            }
            active={activeFilter === 'awaiting_approval'}
            loading={loading}
            attention={safeAwaiting > 0}
            attentionBadge="Review"
            onClick={() =>
              onSelect(activeFilter === 'awaiting_approval' ? 'all' : 'awaiting_approval')
            }
          />
        )}
      </Box>
    </Box>
  );
}

interface LegendChipProps {
  color: string;
  icon: React.ReactNode;
  count: number | undefined;
  pct: number;
  label: string;
  tooltip: string;
  active: boolean;
  loading: boolean;
  onClick: () => void;
  attention?: boolean;
  attentionBadge?: string;
}

function LegendChip({
  color,
  icon,
  count,
  pct,
  label,
  tooltip,
  active,
  loading,
  onClick,
  attention = false,
  attentionBadge,
}: LegendChipProps) {
  const muted = (count ?? 0) === 0 && !attention;
  return (
    <Tooltip title={tooltip} arrow placement="top" enterDelay={500}>
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1,
          borderRadius: 1.25,
          border: '1px solid',
          borderColor: active ? color : 'rgba(15, 10, 7, 0.08)',
          bgcolor: active ? `${color}10` : '#fff',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
          '&:hover': {
            borderColor: color,
            bgcolor: `${color}0a`,
          },
          '&:focus-visible': {
            outline: 'none',
            borderColor: color,
            boxShadow: `0 0 0 3px ${color}33`,
          },
          // Subtle attention pulse on Link Expired when > 0
          ...(attention && {
            borderColor: `${color}55`,
            boxShadow: `0 0 0 3px ${color}12`,
          }),
        }}
      >
        {/* Tone-colored dot + icon disc */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}14`,
            color: color,
            opacity: muted ? 0.55 : 1,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.85, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 20,
                fontWeight: 700,
                color: INK,
                lineHeight: 1,
                letterSpacing: '-0.015em',
                fontVariantNumeric: 'tabular-nums',
                opacity: loading ? 0.35 : muted ? 0.55 : 1,
              }}
            >
              {count !== undefined ? count : '—'}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(15, 10, 7, 0.5)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pct}%
            </Typography>
            {attention && attentionBadge && (
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: color,
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 0.75,
                  bgcolor: `${color}14`,
                  border: `1px solid ${color}33`,
                  ml: 'auto',
                }}
              >
                {attentionBadge}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: muted ? 'rgba(15, 10, 7, 0.4)' : 'rgba(15, 10, 7, 0.55)',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}
